/**
 * 测评关卡 AI 感 loading 浮层：盖在 Detail 页面上
 * - texts 为数组时：按顺序展示，每条 1 秒，总时长 texts.length 秒，进度条填满
 * - text 为字符串时：单条文案，约 2 秒（用于 debug 等）
 */
import React, { useEffect, useRef, useState } from 'react';
import PetAvatar from 'components/PetAvatar';
import './index.scss';

const SINGLE_TEXT_DURATION_MS = 2000;
const SEGMENT_MS = 1200; // 每条文案停留 1.2 秒

const AssessmentAiLoading = ({ visible, text, texts, onComplete }) => {
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const useSequential = visible && texts && Array.isArray(texts) && texts.length > 0;
  const totalDurationMs = useSequential ? texts.length * SEGMENT_MS : SINGLE_TEXT_DURATION_MS;
  const displayText = useSequential ? (texts[currentTextIndex] ?? texts[0]) : text;

  useEffect(() => {
    if (!visible) return;
    if (!useSequential && !text) return;
    startRef.current = Date.now();
    setCurrentTextIndex(0);

    // 文案用 setInterval 固定每秒切一条，保证每条停留满 1 秒
    const intervalIds = [];
    if (useSequential && texts.length > 1) {
      for (let i = 1; i < texts.length; i++) {
        const id = setTimeout(() => setCurrentTextIndex(i), SEGMENT_MS * i);
        intervalIds.push(id);
      }
    }

    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(1, elapsed / totalDurationMs);
      const bar = document.getElementById('assessment-ai-progress-bar');
      if (bar) {
        if (useSequential && texts.length > 0) {
          // 每条文案独立进度条：当前这一秒内 0→1
          const segmentElapsed = elapsed % SEGMENT_MS;
          const segmentPct = Math.min(1, segmentElapsed / SEGMENT_MS);
          bar.style.width = `${getRealisticProgress(segmentPct) * 100}%`;
        } else {
          bar.style.width = `${getRealisticProgress(pct) * 100}%`;
        }
      }

      if (pct < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        onComplete?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      intervalIds.forEach((id) => clearTimeout(id));
    };
  }, [visible, useSequential, totalDurationMs, onComplete, texts?.length]);

  if (!visible) return null;
  if (useSequential && !displayText) return null;
  if (!useSequential && !text) return null;

  return (
    <div className="assessment-ai-loading" role="presentation">
      <div className="assessment-ai-loading__mask" />
      <div className="assessment-ai-loading__content">
        <div className="assessment-ai-loading__avatar">
          <PetAvatar className="assessment-ai-loading__pet" />
        </div>
        <div className="assessment-ai-loading__card">
          <div className="assessment-ai-loading__text">{displayText}</div>
          <div className="assessment-ai-loading__progress">
            <div id="assessment-ai-progress-bar" className="assessment-ai-loading__progress-bar" />
          </div>
        </div>
      </div>
    </div>
  );
};

// 真实感进度：前慢后快、中间有停顿感
function getRealisticProgress(t) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  if (t < 0.15) return t * 2;           // 起步稍慢
  if (t < 0.35) return 0.3 + (t - 0.15) * 0.2;  // 停顿
  if (t < 0.5) return 0.34 + (t - 0.35) * 0.5;
  if (t < 0.65) return 0.415 + (t - 0.5) * 0.1;  // 稍停
  if (t < 0.82) return 0.43 + (t - 0.65) * 0.6;
  if (t < 0.92) return 0.532 + (t - 0.82) * 0.15;
  return 0.547 + (t - 0.92) * 5.66;      // 最后加速到 100%
}

export default AssessmentAiLoading;
